// tienda/PricingSummarySection.jsx
// Concise homepage pricing; full conditions live on /precios.

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { brand, fadeUp } from "../shared/brand";

const SERVICES = [
  {
    label: "Clásico",
    price: "S/ 9.90",
    description: "Entrega estándar para pedidos que tu tienda ya tiene coordinados.",
    benefits: ["Seguimiento operativo", "COD cuando aplique"],
    color: brand.primary,
  },
  {
    label: "Coordinado",
    price: "S/ 14.90",
    description: "Mayor coordinación para reducir fricción y no respuestas.",
    benefits: ["COD y conciliación", "Reintento donde sea elegible"],
    color: brand.accentGold,
    featured: true,
  },
  {
    label: "Premium",
    price: "S/ 19.90",
    description: "Prioridad operativa para pedidos urgentes o sensibles.",
    benefits: ["Beneficios de Coordinado", "Prioridad dentro de la ruta"],
    color: brand.plum,
  },
];

export function PricingSummarySection() {
  return (
    <section id="precios" className="border-b scroll-mt-16" style={{ background: brand.bg, borderColor: brand.line }}>
      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20">
        <motion.div {...fadeUp} className="mx-auto mb-10 max-w-3xl text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.2em]" style={{ color: brand.primary }}>
            Tarifas por pedido
          </p>
          <h2 className="text-3xl font-black md:text-4xl" style={{ color: brand.ink }}>
            Elige el nivel operativo que necesita cada entrega.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed md:text-base" style={{ color: brand.muted }}>
            Empieza con una tarifa clara y sube el nivel de coordinación solo cuando el pedido lo requiera.
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          {SERVICES.map((service, index) => (
            <motion.article
              key={service.label}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: index * 0.06 }}
              className="relative overflow-hidden rounded-2xl border bg-white p-6 shadow-sm"
              style={{
                borderColor: service.featured ? service.color : brand.line,
                boxShadow: service.featured ? `0 22px 55px ${service.color}1F` : undefined,
              }}
            >
              {service.featured && (
                <span
                  className="absolute right-4 top-4 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white"
                  style={{ background: brand.primary }}
                >
                  Recomendado
                </span>
              )}
              <p className="text-sm font-black" style={{ color: service.color }}>{service.label}</p>
              <p className="mt-4 text-4xl font-black" style={{ color: brand.ink }}>{service.price}</p>
              <p className="mt-3 min-h-[42px] text-sm leading-relaxed" style={{ color: brand.muted }}>{service.description}</p>
              <ul className="mt-5 space-y-2.5">
                {service.benefits.map(benefit => (
                  <li key={benefit} className="flex items-center gap-2 text-xs font-bold" style={{ color: brand.ink }}>
                    <CheckCircle2 className="size-4 shrink-0" style={{ color: brand.accentGreen }} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        <motion.div {...fadeUp} className="mt-8 text-center">
          <Link
            to="/precios"
            className="inline-flex items-center justify-center gap-2 rounded-xl border bg-white px-5 py-3 text-sm font-black transition-transform hover:-translate-y-0.5"
            style={{ borderColor: brand.line, color: brand.ink }}
          >
            Comparar servicios y condiciones
            <ArrowRight className="size-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
